import {
  Button,
  Flex,
  Group,
  Modal,
  NumberInput,
  Radio,
  RadioGroup,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconShare3,
  IconTableImport,
  IconUserPlus,
  IconUserUp,
} from "@tabler/icons-react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useParams } from "react-router-dom";
import { useCallback, useState } from "react";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../../stores/LeadsStore";
import { createProspect, createProspectUser } from "../../../../../api/PRMAPI";
import PRMStatusSelector from "../../../../../components/Selectors/PRMStatusSelector";
import BackButton from "../../../../../components/View/BackButton";
import { modals } from "@mantine/modals";
import ManualImport from "../../../lists/import/manualImport/ManualImport";
import { importPrmContacts } from "../../../../../api/ContactAPI";

const LeadsImport = ({ disabled, defaultOrg, onCreated }) => {
  const lang = useLanguage((s) => s.language);
  const { userId } = useParams();
  const updateContainer = useLeadsContainerStore((s) => s.updateContainer);
  const columns = useLeadsStore((s) => s.fields);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});

  const [importState, setImportState] = useState("multiple");

  const handleOpen = () => {
    setOpen(true);
    setLoading(false);
    setDirty(false);

    setErrors({});
  };

  const handleClose = useCallback(
    (isSubmit) => {
      if (dirty && !isSubmit)
        modals.openConfirmModal({
          title: "Attention",
          children: <Text size="sm">{lang.leads.edition_not_saved}</Text>,
          labels: { confirm: lang.global.close, cancel: lang.global.cancel },
          onCancel: () => {},
          onConfirm: () => {
            setOpen(false);
            setDirty(false);
          },
          centered: true,
        });
      else {
        setOpen(false);
        setDirty(false);
      }
    },
    [dirty]
  );

  const onSingleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    let properties = [];
    Object.keys(data).forEach((x) => {
      if (data[x]) {
        if (
          x !== "status" &&
          x !== "id" &&
          columns.find((col) => col.identifier_placeholder === x)?.id
        ) {
          properties.push({
            data_field_id: columns.find(
              (col) => col.identifier_placeholder === x
            )?.id,
            value: data[x],
          });
        }
      }
    });

    const custom_status = parseInt(data.status);
    let contacts = [
      {
        properties,
        custom_status: custom_status || undefined,
        status: custom_status ? "opener" : data.status,
        id: parseInt(data.id),
      },
    ];

    setErrors({});
    setLoading(true);

    console.log(contacts);

    let formData = new FormData();
    var file = new Blob([JSON.stringify(contacts)], {
      type: "application/json",
    });
    formData.append("contacts", file, "data.json");
    importPrmContacts(formData)
      .then((response) => {
        setDirty(false);
        handleClose(true);
        updateContainer(data?.custom_status || data.status, {
          loading: true,
        });
        onCreated?.();
      })
      .catch((error) => {
        let errorResponse = error.response.data.errors;
        let result = {};
        if (errorResponse.find((x) => x?.error?.includes("email"))) {
          result["%email%"] = true;
          document.getElementById("%email%").focus();
        }
        if (errorResponse.find((x) => x?.error?.includes("linkedin"))) {
          result["%linkedin_url%"] = true;
        }

        setErrors(result);
        useLeadsStore.getState().setLoadingJobs(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleImportFinish = useCallback(() => {
    setOpen(false);
    setDirty(false);
    useLeadsStore.getState().setLoadingJobs(true);
    useLeadsContainerStore.getState().setReloadAll(true);
  }, []);

  return (
    <>
      <Button
        leftSection={<IconTableImport size={18} />}
        radius={"md"}
        color="primary"
        onClick={handleOpen}
        size="md"
        disabled={disabled}
        variant={defaultOrg ? "light" : "filled"}
      >
        {lang.prospects.import}
      </Button>

      <Modal
        size={"70%"}
        title={lang.prospects.import_title}
        onClose={handleClose}
        opened={open}
      >
        <Stack
          component={"form"}
          onChange={() => setDirty(true)}
          onSubmit={onSingleSubmit}
          h={"76vh"}
        >
          <Group wrap="nowrap" align="end" gap={"xl"}>
            <Radio
              label={lang.prospects.import_multiple}
              name="list_state"
              size="md"
              checked={importState === "multiple"}
              onChange={(e) => setImportState("multiple")}
            />
            <Radio
              label={lang.prospects.import_single}
              name="list_state"
              checked={importState === "single"}
              size="md"
              onChange={(e) => setImportState("single")}
            />
          </Group>

          {importState === "single" ? (
            <>
              <SimpleGrid
                cols={2}
                flex={"1 1 auto"}
                h={0}
                style={{ overflow: "auto" }}
              >
                <NumberInput label="ID" name="id" size="md" />
                <PRMStatusSelector
                  label={"Status"}
                  required
                  withinPortal={false}
                  name={"status"}
                  id={"status"}
                  size={"md"}
                />
                {columns
                  .sort((a, b) => a.id - b.id)
                  .filter((x) => !x.readonly)
                  .map((column, index) => {
                    return (
                      <TextInput
                        key={column.id}
                        id={column.identifier_placeholder}
                        name={column.identifier_placeholder}
                        label={column.name}
                        placeholder={column.name}
                        defaultValue={
                          column.identifier_placeholder === "%company%"
                            ? defaultOrg
                            : null
                        }
                        type={
                          column.identifier_placeholder === "%email%"
                            ? "email"
                            : "text"
                        }
                        size="md"
                        disabled={column.identifier_placeholder === "id"}
                        error={errors[column.identifier_placeholder]}
                        leftSection={
                          column.created_by && !column.from_sharing ? (
                            <Tooltip label={lang.prm.toolbar.custom_datafield}>
                              <IconUserUp size={18} />
                            </Tooltip>
                          ) : (
                            column.created_by &&
                            column.from_sharing && (
                              <Tooltip label={lang.prm.toolbar.from_sharing}>
                                <IconShare3 size={18} />
                              </Tooltip>
                            )
                          )
                        }
                      />
                    );
                  })}
              </SimpleGrid>

              <Group justify="end">
                <BackButton size="md" onClick={handleClose} />

                <Button
                  loading={loading}
                  leftSection={<IconUserPlus size={18} />}
                  type="submit"
                  size="md"
                >
                  {lang.prm.toolbar.add_prospect_button}
                </Button>
              </Group>
            </>
          ) : (
            <ManualImport
              onFinish={handleImportFinish}
              isCopyPaste={false}
              isPrm
              isActive={"manual"}
            />
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default LeadsImport;
